/**
 * Stone Investment Blog Card
 *
 * Legacy BlogCard implementation with Stone's editorial aesthetic:
 * greyscale images, Cormorant Garamond headings, square corners.
 */

'use client';

import type { BlogCardProps } from '@routeroyal/theme-core';
import Link from 'next/link';
import Image from 'next/image';

export function StoneBlogCard({
  article,
  variant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = true,
  locale = 'en',
  className = '',
}: BlogCardProps) {
  const isCompact = variant === 'compact';
  const isFeatured = variant === 'featured';

  const formattedDate = new Date(article.publishedAt).toLocaleDateString(
    locale === 'fr' ? 'fr-FR' : 'en-GB',
    { day: 'numeric', month: 'long', year: 'numeric' }
  );

  if (isFeatured) {
    return (
      <article className={`group ${className}`}>
        <Link href={`/${locale}/news/${article.slug}`} className="block">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {article.featuredImage && (
              <div className="relative aspect-4/3 overflow-hidden bg-stone-100">
                <Image
                  src={article.featuredImage}
                  alt={article.title}
                  fill
                  className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
              </div>
            )}
            <div className="lg:pl-8">
              {showCategory && article.category && (
                <span
                  className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-4 block"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {article.category}
                </span>
              )}
              <h3
                className="text-2xl lg:text-3xl text-stone-900 mb-4 group-hover:text-stone-500 transition-colors duration-300"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
              >
                {article.title}
              </h3>
              {showExcerpt && article.excerpt && (
                <p
                  className="text-stone-600 text-sm leading-relaxed mb-6 line-clamp-3"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {article.excerpt}
                </p>
              )}
              <div
                className="flex items-center gap-4 text-xs text-stone-500"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {showAuthor && <span>{article.author.name}</span>}
                {showAuthor && <span className="text-stone-300">·</span>}
                <span>{formattedDate}</span>
                {article.readTime && (
                  <>
                    <span className="text-stone-300">·</span>
                    <span>{article.readTime}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className={`group ${className}`}>
      <Link href={`/${locale}/news/${article.slug}`} className="block">
        {article.featuredImage && (
          <div className={`relative ${isCompact ? 'aspect-3/2' : 'aspect-4/3'} overflow-hidden bg-stone-100 mb-5`}>
            <Image
              src={article.featuredImage}
              alt={article.title}
              fill
              className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
            {showCategory && article.category && (
              <div className="absolute top-4 left-4">
                <span
                  className="px-3 py-1 text-[10px] tracking-[0.15em] uppercase text-white bg-stone-900/80 backdrop-blur-sm"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {article.category}
                </span>
              </div>
            )}
          </div>
        )}
        <div>
          <h3
            className={`text-stone-900 mb-3 group-hover:text-stone-500 transition-colors duration-300 ${
              isCompact ? 'text-lg' : 'text-xl'
            }`}
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
          >
            {article.title}
          </h3>
          {showExcerpt && article.excerpt && !isCompact && (
            <p
              className="text-stone-600 text-sm leading-relaxed mb-4 line-clamp-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {article.excerpt}
            </p>
          )}
          <div
            className="flex items-center gap-3 text-xs text-stone-500"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {showAuthor && (
              <>
                <span>{article.author.name}</span>
                <span className="text-stone-300">·</span>
              </>
            )}
            <span>{formattedDate}</span>
          </div>
        </div>
      </Link>
    </article>
  );
}
